import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Typography } from "@mui/material";

import { getCreateJobRoute } from "../../../../../routers/routeUtils";

import SectionFigure from "../../../assets/images/post-football-figure.png";
import { CreateJobIcon, ListItemIcon } from "../../../assets/icons/go-hire-page-icons";

const PostFootballJobsSection = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "goHirePage" });

  const navigate = useNavigate();
  const handleCreateJob = () => navigate(getCreateJobRoute());

  const features = [
    {
      feature: t("structuredPosting"),
      description: t("captureDetailedJobInfo"),
    },
    {
      feature: t("directApplications"),
      description: t("talentsAndProfessionalsCanApplyInstantly"),
    },
    {
      feature: t("aiFitBar"),
      description: t("instantlySeeHowWellApplicantsMatchYourCriteria"),
    },
    {
      feature: t("smartInsights"),
      description: t("autoGeneratedSummaries"),
    },
  ];

  return (
    <Box sx={styles.container} component="section">
      <Box sx={styles.content}>
        <Chip label={t("freeFeatures")} sx={styles.freeFeaturesChip} />
        <Typography sx={styles.title}>{t("postFootballJobsWithPowerfulAIMatching")}</Typography>
        <Typography sx={styles.subtitle}>{t("withGoalngoHiringJourney")}</Typography>
      </Box>
      <Box sx={styles.mainContentContainer}>
        <Box sx={styles.proFeaturesContainer}>
          <Typography sx={styles.proFeaturesTitle}>{t("keyFeatures")}</Typography>
          <Box sx={styles.featuresContainer}>
            {features.map(({ feature, description }, index) => (
              <Feature key={index} feature={feature} description={description} />
            ))}
          </Box>
          <Button
            variant="primary-md"
            sx={styles.createJobButton}
            startIcon={<CreateJobIcon />}
            onClick={handleCreateJob}
          >
            {t("createJob")}
          </Button>
        </Box>
        <Box component="img" src={SectionFigure} alt="Post Football Jobs" sx={styles.sectionFigure} />
      </Box>
    </Box>
  );
};

export default PostFootballJobsSection;

const Feature = ({ feature, description }: { feature: string; description: string }) => {
  return (
    <Box sx={styles.feature}>
      <ListItemIcon />
      <Box sx={styles.featureContent}>
        <Typography sx={styles.featureDescription}>
          <Typography sx={styles.featureTitle}>{feature} </Typography>
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    padding: "24px",
    background:
      "linear-gradient(180deg, rgba(143, 255, 60, 0.08) 0%, rgba(143, 255, 60, 0.00) 30.21%, rgba(143, 255, 60, 0.00) 100%), #FFF",
  },
  mainContentContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: "24px", sm: "0" },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  freeFeaturesChip: {
    width: "fit-content",
    backgroundColor: "var(--white, #FFF)",
    color: "var(--Brand-700, #076D0D)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    padding: "4px 12px",
    borderRadius: "16px",
    border: "1px solid var(--Brand-400, #00E606)",
    "& .MuiChip-label": {
      padding: "0",
    },
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
  },
  subtitle: {
    color: "var(--Gray-Gray-700, #5E6278)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  proFeaturesTitle: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  proFeaturesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  featuresContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: { xs: "100%", md: "414px" },
  },
  feature: {
    display: "flex",
    gap: "12px",
  },
  featureTitle: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    paddingRight: "6px",
    display: "inline-block",
  },
  featureContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  featureDescription: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    display: "inline-block",
  },
  createJobButton: {
    width: { xs: "100%", sm: "fit-content" },
    height: "fit-content",
  },
  sectionFigure: {
    display: { xs: "none", md: "block" },
    width: "382px",
    height: "284px",
  },
};
